import React from 'react';
import { Resources, AppPermissions } from '@erecruit/types';
import {
  createColumns,
  TableWidgetResource,
  setFormatColumns,
  setRenderEmptyState,
  PositionSearchRequestWithDataPref,
  PositionSearchCountRequestWithDataPref,
  MatchSearchCountRequestWithDataPref,
  MatchSearchRequestWithDataPref,
  ShiftSearchCountRequestWithDataPref,
  ShiftSearchRequestWithDataPref,
  SearchTypeId,
} from '@erecruit/modules';
import { Button, ColumnProps, EmptyState, Flex, Icon } from '@erecruit/ui-kit';
import { useHistory } from 'react-router';
import { Intl } from '@erecruit/core';

import { generateColumns as generatePositionColumns } from '../../components/PositionSearch/columns';
import { generateColumns as generateMatchColumns } from '../../components/MatchSearch/columns';
import { generateColumns as generateShiftsColumns } from '../../components/ShiftSearch/columns';
import { MatchParams } from '../../App/types';

const mapSearchTypeToMatchType = (searchTypeId: SearchTypeId): MatchParams => {
  switch (searchTypeId) {
    case Resources.MatchSearchType.Submittals:
      return 'Submittal';
    default:
      return 'All';
  }
};

/**
 * Customize List widget cell rendering for the application.
 * @param config
 */
const formatColumns = (config: TableWidgetResource) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let columns: ColumnProps<any>[] = [];

  switch (config.searchType) {
    case 'Positions':
      columns = generatePositionColumns(
        () => undefined,
        () => undefined,
        () => undefined,
        {},
        '',
      );
      break;
    case 'Matches':
      columns = generateMatchColumns(
        () => undefined,
        () => undefined,
        {},
        '',
        undefined,
        mapSearchTypeToMatchType(config.searchTypeId),
      );
      break;
    case 'Shifts':
      columns = generateShiftsColumns(
        () => undefined,
        () => undefined,
        {},
      );
      break;
    default:
      throw Error(`Unknow ListWidget search type: ${config.searchType}.`);
  }

  if (!columns || !config.columns) {
    return [];
  }

  // Pick column props needed
  return createColumns(
    config.columns.map(columnKey => {
      const col = columns.find(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ({ key }: ColumnProps<any>) => key === columnKey,
      );
      if (col) {
        return {
          key: col.key,
          render: col.render,
          title: col.title,
          dataIndex: col.dataIndex,
        };
      }
      return { key: columnKey };
    }),
  );
};

const TableEmptyState = Intl.injectIntl<{ searchTypeId?: string | number }>(
  ({ searchTypeId, intl }) => {
    const history = useHistory();
    const isOpenPosition =
      searchTypeId === nameof<Resources.PositionSearchType.Open>();
    const children = isOpenPosition ? (
      <Button type="primary" onClick={() => history.push('/requestPosition')}>
        {intl.formatMessage({
          id: messageIds.vms.positions.createPosition,
        })}
        <Icon color="bodyTertiary" type="plus" textual></Icon>
      </Button>
    ) : null;

    const description = isOpenPosition
      ? intl.formatMessage({
          id: messageIds.vms.positionSearch.noDataMessage,
        })
      : intl.formatMessage({
          id: messageIds.modules.widget.tableWidget.noDataMessage,
        });

    return (
      <Flex stretch align="center" justify="center" padding={[0, 0, 12, 0]}>
        <EmptyState
          description={description}
          image={<Icon type="empty-state" size="large" />}
          children={children}
        />
      </Flex>
    );
  },
);

const renderEmptyState = (config: TableWidgetResource) => (
  <TableEmptyState searchTypeId={config.searchTypeId} />
);
setRenderEmptyState(renderEmptyState);
setFormatColumns(formatColumns);

const PositionSearch = {
  id: nameof<Resources.PositionSearch>(o => o.id),
  name: nameof<Resources.PositionSearch>(o => o.name),
  position: nameof.full<Resources.PositionSearch>(o => o.positionType.name),
  company: nameof.full<Resources.PositionSearch>(o => o.company.name),
  createdOn: nameof<Resources.PositionSearch>(o => o.createdOn),
  status: nameof.full<Resources.PositionSearch>(o => o.positionStatus.name),
};

const MatchSearch = {
  id: nameof<Resources.MatchSearch>(o => o.id),
  position: nameof.full<Resources.MatchSearch>(o => o.position.name),
  status: nameof.full<Resources.MatchSearch>(o => o.status.name),
  positionType: nameof.full<Resources.MatchSearch>(o => o.positionType.name),
  candidate: nameof.full<Resources.MatchSearch>(o => o.candidate.name),
  startDate: nameof<Resources.MatchSearch>(o => o.estimatedStartDate),
  endDate: nameof<Resources.MatchSearch>(o => o.estimatedEndDate),
  submittedOn: nameof<Resources.MatchSearch>(o => o.submittedOn),
};

const ShiftSearch = {
  candidate: nameof.full<Resources.ShiftSearch>(o => o.candidate.name),
  position: nameof.full<Resources.ShiftSearch>(o => o.position.name),
  shiftDate: nameof<Resources.ShiftSearch>(o => o.start),
  shiftTime: 'shiftTime',
  status: nameof.full<Resources.ShiftSearch>(o => o.shiftStatus.name),
};

export const tableWidgets: TableWidgetResource[] = [
  new TableWidgetResource({
    widgetId: 'Table.Positions.Open',
    searchType: 'Positions',
    searchTypeId: Resources.PositionSearchType.Open,
    searchRoute: `/positions?positionType=${nameof<
      Resources.PositionSearchType.Open
    >()}`,
    iconType: 'positions',
    label: messageIds.modules.widget.tableWidget.openPositions,
    permission: AppPermissions.ViewPositions,
    resourceRequest: [
      new PositionSearchRequestWithDataPref({
        filter: {
          positionSearchType: Resources.PositionSearchType.Open,
        },
      }),
      new PositionSearchCountRequestWithDataPref({
        filter: {
          positionSearchType: Resources.PositionSearchType.Open,
        },
      }),
    ],
    optionalColumns: [
      PositionSearch.createdOn,
      PositionSearch.position,
      PositionSearch.status,
      PositionSearch.company,
    ],
    visibleColumns: [PositionSearch.name],
    columns: [
      PositionSearch.name,
      PositionSearch.position,
      PositionSearch.status,
      PositionSearch.company,
      PositionSearch.createdOn,
    ],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Positions.Closed',
    searchType: 'Positions',
    searchTypeId: Resources.PositionSearchType.Closed,
    searchRoute: `/positions?positionType=${nameof<
      Resources.PositionSearchType.Closed
    >()}`,
    iconType: 'closed-position',
    label: messageIds.modules.widget.tableWidget.closedPositions,
    permission: AppPermissions.ViewPositions,
    resourceRequest: [
      new PositionSearchRequestWithDataPref({
        filter: {
          positionSearchType: Resources.PositionSearchType.Closed,
        },
      }),
      new PositionSearchCountRequestWithDataPref({
        filter: {
          positionSearchType: Resources.PositionSearchType.Closed,
        },
      }),
    ],
    optionalColumns: [
      PositionSearch.createdOn,
      PositionSearch.position,
      PositionSearch.status,
      PositionSearch.company,
    ],
    visibleColumns: [PositionSearch.name],
    columns: [
      PositionSearch.name,
      PositionSearch.position,
      PositionSearch.status,
      PositionSearch.company,
      PositionSearch.createdOn,
    ],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Positions.PendingMyApproval',
    searchType: 'Positions',
    searchTypeId: Resources.PositionSearchType.PendingMyApproval,
    searchRoute: `/positions?positionType=${nameof<
      Resources.PositionSearchType.PendingMyApproval
    >()}`,
    iconType: 'approvals',
    label: messageIds.modules.widget.tableWidget.pendingMyApprovalPositions,
    permission: AppPermissions.ViewPositions,
    resourceRequest: [
      new PositionSearchRequestWithDataPref({
        filter: {
          positionSearchType: Resources.PositionSearchType.PendingMyApproval,
        },
      }),
      new PositionSearchCountRequestWithDataPref({
        filter: {
          positionSearchType: Resources.PositionSearchType.PendingMyApproval,
        },
      }),
    ],
    optionalColumns: [
      PositionSearch.position,
      PositionSearch.status,
      PositionSearch.company,
    ],
    visibleColumns: [PositionSearch.name],
    columns: [
      PositionSearch.name,
      PositionSearch.position,
      PositionSearch.company,
      PositionSearch.status,
    ],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Matches.Working',
    searchType: 'Matches',
    searchTypeId: Resources.MatchSearchType.Working,
    iconType: 'match',
    searchRoute: `/matches?matchType=${nameof(
      Resources.MatchStatusType.FullPlacement,
    )}`,
    label: messageIds.modules.widget.tableWidget.currentlyWorkingMatches,
    permission: AppPermissions.ViewMatches,
    resourceRequest: [
      new MatchSearchRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.Working,
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.Working,
        },
      }),
    ],
    optionalColumns: [
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.startDate,
      MatchSearch.endDate,
    ],
    columns: [
      MatchSearch.id,
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.startDate,
      MatchSearch.endDate,
    ],
    visibleColumns: [MatchSearch.id],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Matches.Submittals',
    searchType: 'Matches',
    searchTypeId: Resources.MatchSearchType.Submittals,
    searchRoute: `/matches?matchType=Submittal`,
    iconType: 'users',
    label: messageIds.modules.widget.tableWidget.submittedMatches,
    permission: AppPermissions.ViewMatches,
    resourceRequest: [
      new MatchSearchRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.Submittals,
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.Submittals,
        },
      }),
    ],
    optionalColumns: [
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.status,
      MatchSearch.positionType,
      MatchSearch.submittedOn,
    ],
    columns: [
      MatchSearch.id,
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.status,
      MatchSearch.positionType,
      MatchSearch.submittedOn,
    ],
    visibleColumns: [MatchSearch.id],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Matches.PendingStart',
    searchType: 'Matches',
    searchTypeId: Resources.MatchSearchType.PendingStart,
    searchRoute: `/matches?matchType=${nameof<
      Resources.MatchSearchType.PendingStart
    >()}`,
    iconType: 'check',
    label: messageIds.modules.widget.tableWidget.pendingStartMatches,
    permission: AppPermissions.ViewMatches,
    resourceRequest: [
      new MatchSearchRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.PendingStart,
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.PendingStart,
        },
      }),
    ],
    optionalColumns: [
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.startDate,
    ],
    columns: [
      MatchSearch.id,
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.startDate,
    ],
    visibleColumns: [MatchSearch.id],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Matches.PendingMyApproval',
    searchType: 'Matches',
    searchTypeId: Resources.MatchSearchType.PendingMyApproval,
    searchRoute: `/matches?matchType=${nameof<
      Resources.MatchSearchType.PendingMyApproval
    >()}`,
    iconType: 'approvals',
    label: messageIds.modules.widget.tableWidget.pendingMyApprovalMatches,
    permission: AppPermissions.ViewMatches,
    resourceRequest: [
      new MatchSearchRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.PendingMyApproval,
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.PendingMyApproval,
        },
      }),
    ],
    optionalColumns: [
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.status,
    ],
    columns: [
      MatchSearch.id,
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.status,
    ],
    visibleColumns: [MatchSearch.id],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Matches.EndingSoon',
    searchType: 'Matches',
    searchTypeId: Resources.MatchSearchType.EndingSoon,
    searchRoute: `/matches?matchType=${nameof<
      Resources.MatchSearchType.EndingSoon
    >()}`,
    iconType: 'time',
    label: messageIds.modules.widget.tableWidget.endingSoonMatches,
    permission: AppPermissions.ViewMatches,
    resourceRequest: [
      new MatchSearchRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.EndingSoon,
        },
      }),
      new MatchSearchCountRequestWithDataPref({
        filter: {
          matchSearchType: Resources.MatchSearchType.EndingSoon,
        },
      }),
    ],
    optionalColumns: [
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.endDate,
    ],
    columns: [
      MatchSearch.id,
      MatchSearch.candidate,
      MatchSearch.position,
      MatchSearch.endDate,
    ],
    visibleColumns: [MatchSearch.id],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Shifts.Filled',
    searchType: 'Shifts',
    searchTypeId: Resources.ShiftSearchType.Filled,
    searchRoute: `/shifts?shiftType=${nameof<
      Resources.ShiftSearchType.Filled
    >()}`,
    iconType: 'filled-shift',
    label: messageIds.modules.widget.tableWidget.filledShifts,
    permission: AppPermissions.ViewShifts,
    resourceRequest: [
      new ShiftSearchRequestWithDataPref({
        filter: { shiftSearchType: Resources.ShiftSearchType.Filled },
      }),
      new ShiftSearchCountRequestWithDataPref({
        filter: { shiftSearchType: Resources.ShiftSearchType.Filled },
      }),
    ],
    visibleColumns: [
      ShiftSearch.candidate,
      ShiftSearch.position,
      ShiftSearch.shiftDate,
      ShiftSearch.status,
      ShiftSearch.shiftTime,
    ],
    columns: [
      ShiftSearch.candidate,
      ShiftSearch.position,
      ShiftSearch.shiftDate,
      ShiftSearch.status,
      ShiftSearch.shiftTime,
    ],
    optionalColumns: [],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Shifts.UnFilled',
    searchType: 'Shifts',
    searchTypeId: Resources.ShiftSearchType.UnFilled,
    searchRoute: `/shifts?shiftType=${nameof<
      Resources.ShiftSearchType.UnFilled
    >()}`,
    iconType: 'calendar',
    label: messageIds.modules.widget.tableWidget.unfilledShifts,
    permission: AppPermissions.ViewShifts,
    resourceRequest: [
      new ShiftSearchRequestWithDataPref({
        filter: { shiftSearchType: Resources.ShiftSearchType.UnFilled },
      }),
      new ShiftSearchCountRequestWithDataPref({
        filter: { shiftSearchType: Resources.ShiftSearchType.UnFilled },
      }),
    ],
    visibleColumns: [
      ShiftSearch.position,
      ShiftSearch.shiftDate,
      ShiftSearch.status,
      ShiftSearch.shiftTime,
    ],
    columns: [
      ShiftSearch.position,
      ShiftSearch.shiftDate,
      ShiftSearch.status,
      ShiftSearch.shiftTime,
    ],
    optionalColumns: [],
  }),
  new TableWidgetResource({
    widgetId: 'Table.Shifts.Cancelled',
    searchType: 'Shifts',
    searchTypeId: Resources.ShiftSearchType.Cancelled,
    searchRoute: `/shifts?shiftType=${nameof<
      Resources.ShiftSearchType.Cancelled
    >()}`,
    iconType: 'cancelled-shift',
    label: messageIds.modules.widget.tableWidget.cancelledShifts,
    permission: AppPermissions.ViewShifts,
    resourceRequest: [
      new ShiftSearchRequestWithDataPref({
        filter: { shiftSearchType: Resources.ShiftSearchType.Cancelled },
      }),
      new ShiftSearchCountRequestWithDataPref({
        filter: { shiftSearchType: Resources.ShiftSearchType.Cancelled },
      }),
    ],
    visibleColumns: [
      ShiftSearch.position,
      ShiftSearch.candidate,
      ShiftSearch.shiftDate,
      ShiftSearch.shiftTime,
      ShiftSearch.status,
    ],
    columns: [
      ShiftSearch.position,
      ShiftSearch.shiftDate,
      ShiftSearch.shiftTime,
      ShiftSearch.status,
    ],
    optionalColumns: [],
  }),
];
